import React, { useEffect, useState } from 'react';
import { fetchPublicRegistrations, PublicRegistration } from '../utils/publicRegistrations';
import StatusIndicator from '../components/StatusIndicator';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, Alert, Switch, FormControlLabel, Button } from '@mui/material';
import { useEventEdition } from '../contexts/EventEditionContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '../hooks/useLocalizedField';
import { useEditionFromUrl } from '../hooks/useEditionFromUrl';

interface PublicRegistrationsPageProps {
  /** 
   * The event edition ID in the format '{eventId}-{edition}[-suffix]' 
   * Example: 'mo-2025' or 'kutc-2025-turklasse'
   * - eventId: The stable, unique identifier for the event (e.g., 'mo' for Malvikingen Opp)
   * - edition: The year or edition identifier (e.g., '2025')
   * - suffix: Optional suffix for specific registration classes (e.g., 'turklasse')
   */
  editionId?: string;
}

const PublicRegistrationsPage: React.FC<PublicRegistrationsPageProps> = ({ editionId }) => {
  const { event, loading: eventLoading, error: eventError } = useEventEdition();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  // Sync EventEditionContext from URL query params (?series=... or ?editionId=...).
  // This lets external entry points like https://runnershub.krultra.no/participants?series=kutc
  // land on the correct edition without relying on prior navigation/localStorage state.
  const { resolving: urlResolving, resolveError: urlResolveError } = useEditionFromUrl();
  const [registrations, setRegistrations] = useState<PublicRegistration[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);

  // Load registrations when event is available or when editionId changes
  useEffect(() => {
    const loadRegistrations = async (id: string) => {
      setLoading(true);
      try {
        const data = await fetchPublicRegistrations(id);
        setRegistrations(data);
      } catch (error) {
        console.error('Error loading registrations:', error);
      } finally {
        setLoading(false);
      }
    };

    if (event?.id) {
      loadRegistrations(event.id);
    } else if (editionId) {
      // If we have an editionId but no event, use it directly
      // This assumes fetchPublicRegistrations can handle the editionId format
      loadRegistrations(editionId);
    }
  }, [event?.id, editionId]);

  if (eventLoading || urlResolving) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (eventError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {t('participants.errors.loadingEvent', { message: eventError.message })}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back to Home
        </Button>
      </Container>
    );
  }

  if (urlResolveError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{urlResolveError}</Alert>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('participants.errors.selectEvent')}
        </Button>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          {t('participants.errors.noEvent')}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('participants.errors.selectEvent')}
        </Button>
      </Container>
    );
  }

  const regsList = registrations || [];
  const filteredRegs = regsList.filter((r) => {
    if (showCancelled) return true;
    const normalizedStatus = String(r.status || '').toLowerCase();
    return normalizedStatus === 'pending' || normalizedStatus === 'confirmed';
  });
  const now = new Date();
  const isExpiredWl = (r: PublicRegistration) => !!(r.isOnWaitinglist && r.waitinglistExpires && r.waitinglistExpires <= now);
  // Display lists (include cancelled when toggled on)
  const participants = filteredRegs.filter(r => !r.isOnWaitinglist);
  const waitingList = filteredRegs.filter(r => r.isOnWaitinglist && (showCancelled || !isExpiredWl(r)));
  // Stable indices computed from ACTIVE entries only
  const activeParticipants = regsList.filter(r => ['pending','confirmed'].includes(r.status) && !r.isOnWaitinglist);
  const activeWaiting = regsList.filter(r => ['pending','confirmed'].includes(r.status) && r.isOnWaitinglist && !isExpiredWl(r));
  const participantIndexByRegNo = new Map<number, number>();
  activeParticipants.forEach((r, i) => participantIndexByRegNo.set(r.registrationNumber, i + 1));
  const waitingIndexByRegNo = new Map<number, number>();
  activeWaiting.forEach((r, i) => waitingIndexByRegNo.set(r.registrationNumber, i + 1));

  const formatIndexOrBib = (reg: PublicRegistration, indexMap: Map<number, number>) => {
    if (reg.bib !== undefined && reg.bib !== null && reg.bib !== '') {
      return reg.bib;
    }
    return indexMap.get(reg.registrationNumber) ?? '';
  };
  const usesBibNumbers = regsList.some(r => r.bib !== undefined && r.bib !== null && r.bib !== '');
  // Counts
  const participantsCount = showCancelled ? participants.length : activeParticipants.length;
  const waitingListCount = showCancelled ? waitingList.length : activeWaiting.length;
  const hasWaitingList = waitingList.length > 0;
  const getLocalizedDistanceName = (distanceId: string) => {
    const distance = (event.raceDistances || []).find(d => d.id === distanceId);
    return distance ? getLocalizedField(distance, 'displayName') : distanceId;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
        {t(hasWaitingList ? 'participants.titleWithWaitingList' : 'participants.title', { eventName: event.eventName, edition: event.edition })}
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        {t(hasWaitingList ? 'participants.descriptionWithWaitingList' : 'participants.description')}
      </Alert>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography>{t('participants.participantsCount', { count: participantsCount })} &nbsp; {t('participants.waitingListCount', { count: waitingListCount })}</Typography>
        <FormControlLabel control={<Switch checked={showCancelled} onChange={e => setShowCancelled(e.target.checked)} />} label={t('participants.showCancelled')} />
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 180 }}>
          <CircularProgress />
        </Box>
      ) : registrations && registrations.length > 0 ? (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, boxShadow: 2 }}>
          <Table stickyHeader aria-label="public registrations table">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.paper' }}>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'info.dark', color: 'info.contrastText', borderBottom: 2, borderColor: 'info.dark' }}>{t(`participants.tableHeaders.${usesBibNumbers ? 'bib' : 'number'}`)}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'info.dark', color: 'info.contrastText', borderBottom: 2, borderColor: 'info.dark' }}>{t('participants.tableHeaders.name')}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'info.dark', color: 'info.contrastText', borderBottom: 2, borderColor: 'info.dark' }}>{t('participants.tableHeaders.nationality')}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'info.dark', color: 'info.contrastText', borderBottom: 2, borderColor: 'info.dark' }}>{t('participants.tableHeaders.representing')}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'info.dark', color: 'info.contrastText', borderBottom: 2, borderColor: 'info.dark' }}>{t('participants.tableHeaders.raceDistance')}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'info.dark', color: 'info.contrastText', borderBottom: 2, borderColor: 'info.dark' }}>{t('participants.tableHeaders.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {participants.map((reg, idx) => (
                <TableRow
                  key={`participant-${reg.id}`}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? 'background.default' : 'action.hover',
                    '&:last-child td, &:last-child th': { border: 0 }
                  }}
                >
                  <TableCell>{formatIndexOrBib(reg, participantIndexByRegNo)}</TableCell>
                  <TableCell>{reg.firstName} {reg.lastName}</TableCell>
                  <TableCell>{reg.nationality}</TableCell>
                  <TableCell>{reg.representing}</TableCell>
                  <TableCell>{getLocalizedDistanceName(reg.raceDistance) || '-'}</TableCell>
                  <TableCell><StatusIndicator status={reg.status} /></TableCell>
                </TableRow>
              ))}
              {hasWaitingList && (
                <>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', fontWeight: 'bold', bgcolor: 'info.dark', color: 'info.contrastText' }}>
                      {t('participants.waitingListSection')}
                    </TableCell>
                  </TableRow>
                  {waitingList.map((reg, idx) => {
                    const expired = isExpiredWl(reg);
                    const displayStatus = expired ? 'expired' : reg.status;
                    return (
                    <TableRow
                      key={`waiting-${reg.id}`}
                      sx={{
                        backgroundColor: idx % 2 === 0 ? 'background.default' : 'action.hover',
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}
                    >
                      <TableCell>{!expired ? formatIndexOrBib(reg, waitingIndexByRegNo) : ''}</TableCell>
                      <TableCell>{reg.firstName} {reg.lastName}</TableCell>
                      <TableCell>{reg.nationality}</TableCell>
                      <TableCell>{reg.representing}</TableCell>
                      <TableCell>{getLocalizedDistanceName(reg.raceDistance) || '-'}</TableCell>
                      <TableCell><StatusIndicator status={displayStatus} /></TableCell>
                    </TableRow>
                  );})}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary" sx={{ mt: 4, fontStyle: 'italic' }}>{t('participants.noParticipants')}</Typography>
      )}
    </Container>
  );
};

export { PublicRegistrationsPage };
export default PublicRegistrationsPage;
